/**
 * Auth Page v1.8 - Enterprise Authentication (v1.8 2026-10-07, cc_tasks row 494 / T534: a browser that has never
 *   signed in here and was sent to a signed-in page opens on Create account, with one line on what they get for
 *   that page (src/lib/signup-destination.ts); after Create Account the form is replaced by a "Check your email at
 *   <address>" panel with Resend and "Wrong address? Change it". v1.7 2026-10-07, cc_tasks row 491 / T531: "Claim it free" (?showwork=)
 *   opens on Create account, not Sign in; a claimed check is marked as a destination so the onboarding gate lets the
 *   claimed report open first, as the claim button promises; tooltips on the mode switch and the magic link button.)
 * (v1.6 2026-09-30, cc_tasks row 310 / T344: where the person was going comes
 *   FIRST for every provider, onboarding after (src/lib/post-auth-route.ts decidePostAuthRoute). The post-auth handler
 *   runs once per sign-in (it ran twice and the second run sent people to onboarding), a destination that is a static
 *   page (/pricing) is a full page load, and the magic link / confirmation email carry ?next=. v1.5 2026-09-30, cc_tasks row 297 / T332: a failed LinkedIn/Google
 *   return shows "Sign-in with <provider> didn't finish. Try again, or use email." (src/lib/oauth-return-error.ts);
 *   the sign-up form carries a hidden honeypot field that register-account checks. v1.4 2026-09-30, cc_tasks row 284 / T319: the funnel's
 *   signup_start is the email form submitted or an OAuth button pressed, not arriving here; Continue with
 *   Google goes through the auth server and shows only while Google is switched on there. v1.3 2026-09-29: Continue with LinkedIn, cc_tasks row 260; v1.2 2026-09-27: ?email= prefill from public-page boxes)
 * WHY: Professional, Linear-inspired auth experience with smart returning user detection
 * CHANGES: Uses useAuth singleton hook to prevent login flash on new tabs.
 *          Shows spinner while session resolves instead of rendering login form.
 *
 * Features:
 * - Toggle between Sign In / Sign Up modes
 * - Google OAuth (prominent)
 * - LinkedIn sign-in (same destination-keeping path, src/lib/linkedin-signin.ts)
 * - Magic Link (secondary)
 * - Email + Password with validation
 * - Returning user detection
 * - Password recovery link
 * - Animated gradient borders
 * - Smooth transitions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { readStoredInviteRef, claimStoredInvite } from "@/lib/inviteAttribution";
import { takeCheckoutIntent, resumeCheckout } from "@/lib/checkout-intent";
import { recordOAuthSignupCompleted, startOAuthSignIn, googleSignInAvailable, GOOGLE_TOOLTIP } from "@/lib/linkedin-signin";
import { recordSignupStart, recordSignupPageView } from "@/lib/signup-funnel";
import { oauthErrorNotice } from "@/lib/oauth-return-error";
import { decidePostAuthRoute, sanitizeNext, stashNext, peekStoredNext, clearStoredNext, markOnboardingDeferred, intentQuery } from "@/lib/post-auth-route";
import { signupDestinationCopy, browserHasSignedInHere } from "@/lib/signup-destination";
import { LinkedInSignInButton } from "@/components/auth/LinkedInSignInButton";
import { trackEvent } from "@/lib/analytics";
import { ignoreSupabaseError } from "@/lib/supabase-error";
import mentionfoxLogo from "@/assets/mentionfox-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseKeys";
import { useAuth } from "@/hooks/useAuth";
import {
  signUpSchema,
  signInSchema,
  magicLinkSchema,
  type SignUpFormData,
  type SignInFormData
} from "@/lib/validations";
import {
  checkExistingUser,
  storeLastAuthMethod,
  ensureProfile,
  getAuthMethodLabel,
  type AuthMethod
} from "@/lib/auth-helpers";
import {
  Mail,
  Lock,
  User,
  Building2,
  ArrowRight,
  Loader2,
  Sparkles,
  CheckCircle2
} from "lucide-react";
import { flushPendingFundingSaves } from "@/lib/flushPendingFundingSaves";

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

type AuthMode = "signin" | "signup" | "magic_link";

// Default-tab detection at module level so the initial useState evaluates
// with the right value — avoids a "Sign In first paint" flash for trial
// arrivals. Returns "signup" when the URL signals the user came from a
// signup-intent surface (landing page CTA, persona link, report footer,
// explicit ?signup=true), otherwise "signin".
function detectInitialAuthMode(): AuthMode {
  if (typeof window === "undefined") return "signin";
  try {
    const params = new URLSearchParams(window.location.search);
    // T573: back from their own confirmation link, the account exists. Sign in, never the sign-up form
    // again (which asked a confirmed person for their name, and refused them without it).
    if (params.get("verified") === "true") return "signin";
    if (params.get("plan")) return "signup";
    if (params.get("persona")) return "signup";
    if (params.get("trial")) return "signup";
    if ((params.get("signup") || "").toLowerCase() === "true") return "signup";
    // T531: "Claim it free" after a free check says "Create your free account"; it arrives with ?showwork=.
    if (params.get("showwork")) return "signup";
    const ref = document.referrer || "";
    if (/\/geofixer-trial/i.test(ref)) return "signup";
    if (/\/reports\/state-of-ai-citation/i.test(ref)) return "signup";
    // T534: sent here from a signed-in page by a browser that has never signed in: most such people are new.
    if (sanitizeNext(params.get("next") || params.get("return_to") || params.get("redirect")) && !browserHasSignedInHere()) return "signup";
  } catch { /* fall through to signin */ }
  return "signin";
}

// REWARD-FIRST: the page the visitor was trying to reach before auth bounced them (e.g. the
// "Get a free snapshot" link → the vetting suite prefilled with their subject). We read it from
// the ?next= return target the auth guards now attach; if that was lost across an OAuth redirect
// we reconstruct it from the localStorage subject stash the homepage set. Internal paths only —
// never an absolute/external URL (open-redirect guard). Caller consumes-and-clears the stash.
function getRewardTarget(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    // TWO NAMES FOR ONE THING, AND ONLY ONE OF THEM WAS BEING READ (2026-09-10).
    //
    // This read `?next=`. RequireAuth in App.tsx has always sent people here as
    // `/auth?return_to=<path>` — a different spelling of the identical idea —
    // and nothing anywhere read it back. So every visitor bounced off a gated
    // route landed on the dashboard or in onboarding, whatever they had
    // clicked, and the parameter that was supposed to bring them back was
    // decoration.
    //
    // Both names resolve here, through the same open-redirect guard, so the
    // public /funding officer cards can link to the convention the app already
    // emits instead of inventing a third.
    // T344: one sanitizer for every next= (src/lib/post-auth-route.ts sanitizeNext): same-origin
    // path only, no "//", no backslash, no scheme, no encoded tricks, never back to /auth.
    // A still-encoded "%2F..." from an older link is decoded once there, as before.
    // T1103: ?redirect= too. The 20 /dens/*.html pages send /auth?redirect=/dashboard/dens/<den> and nothing read it,
    // so a new account from a Den page landed in onboarding step 1 instead of its Den.
    const next = sanitizeNext(params.get("next") || params.get("return_to") || params.get("redirect"));
    if (next) return next;
  } catch { /* ignore */ }
  // A ?next= that did not survive the trip. Google sign-in returns to the bare
  // origin, so the query string is gone by the time we get back — and an intent
  // that dies at the login door is the same failure as no intent at all. The
  // consent screen stashes where it was going before it sends anyone here.
  {
    const stashed = peekStoredNext();
    if (stashed) { clearStoredNext(); return stashed; }
  }
  try {
    const subj = (localStorage.getItem("mf_pending_subject") || "").trim();
    // Only honor a FRESH stash (<60 min) so an abandoned intent can't hijack a much-later login.
    const at = Number(localStorage.getItem("mf_pending_subject_at") || "0");
    const fresh = at > 0 && (Date.now() - at) < 60 * 60 * 1000;
    if (subj && fresh) return "/dashboard/vetting-suite?prefill=true&subject_name=" + encodeURIComponent(subj);
  } catch { /* ignore */ }
  return null;
}

// The front-page check they watched run, carried across the signup round trip. The
// token is on the CTA link; localStorage is the fallback for a sign-in that comes
// back to a bare origin (Google) with the query string gone.
const SUPABASE_FN_BASE = "https://wnzxffwiiubqvcvnnjbz.supabase.co/functions/v1";
function getShowworkRun(): string | null {
  const ok = (v: string | null) => (v && /^[a-f0-9]{32}$/.test(v) ? v : null);
  try {
    const fromUrl = ok(new URLSearchParams(window.location.search).get("showwork"));
    if (fromUrl) return fromUrl;
  } catch { /* ignore */ }
  try {
    const stored = ok(localStorage.getItem("mf_showwork_run"));
    if (stored) return stored;
  } catch { /* ignore */ }
  // A run started from an in-content offer on a public page (mf_entry_intent,
  // written by the front page) that never reached the gate — an open result, or
  // a visitor who signed up from the nav instead of the gate's button. The
  // account they create still lands on THAT run first. A day old is too old.
  try {
    const ent = JSON.parse(localStorage.getItem("mf_entry_intent") || "null");
    if (ent && Date.now() - Number(ent.at || 0) < 24 * 60 * 60 * 1000) return ok(String(ent.run || ""));
  } catch { /* a malformed stash is simply no intent */ }
  return null;
}

// T821: the Buyers Now watch intent kept in this browser by public/buyers.js before sign-up. Consumed once.
function getBuyersWatch(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem("mf_buyers_watch") || "null");
    localStorage.removeItem("mf_buyers_watch");
    if (v && /^[a-f0-9]{20,64}$/.test(String(v.run)) && Date.now() - Number(v.at || 0) < 2 * 60 * 60 * 1000) {
      return `/buyers?run=${v.run}&watch=1`;
    }
  } catch { /* storage blocked or malformed: no intent */ }
  return null;
}

export default function Auth() {
  const navigate = useNavigate();
  const { session, user, isReady } = useAuth();
  const [mode, setMode] = useState<AuthMode>(() => detectInitialAuthMode());
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // T319: Google shows only while the auth server has it switched on (a dead button is worse than none).
  const [googleOn, setGoogleOn] = useState(false);
  useEffect(() => {
    let live = true;
    void googleSignInAvailable().then((on) => { if (live) setGoogleOn(on); });
    return () => { live = false; };
  }, []);
  // T332: a LinkedIn/Google sign-in that came back with an error (main.tsx routed it here).
  const [oauthNotice] = useState<string | null>(() => oauthErrorNotice());
  // T344: the user id handlePostAuth already routed. It is reached from two places (the
  // "already signed in" effect and the SIGNED_IN listener); the second run used to find the
  // destination already consumed and send the person to onboarding.
  const postAuthFor = useRef<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // The account exists and the confirmation did not go out. A separate state
  // from magicLinkSent because it is the opposite news and needs its own button.
  const [confirmSendFailed, setConfirmSendFailed] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  // T968: an account whose email is not confirmed yet, met at sign-in: it gets the link again here (was a dead end).
  const [unconfirmedEmail, setUnconfirmedEmail] = useState("");
  const [unconfirmedSent, setUnconfirmedSent] = useState(false);
  // T534: the address a new account's confirmation went to; while set, the form is replaced by the panel.
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);

  // Form state
  // ?email= prefills the address a visitor already typed into a box on a public page (the
  // weekly report box, cc_tasks row 58), so they do not type it twice.
  const [email, setEmail] = useState(() => {
    try {
      const e = (new URLSearchParams(window.location.search).get("email") || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e.slice(0, 254) : "";
    } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Returning user state
  const [returningUser, setReturningUser] = useState<{
    exists: boolean;
    lastAuthMethod?: AuthMethod;
  } | null>(null);
  const [checkingUser, setCheckingUser] = useState(false);

  // Remember me
  const [rememberMe, setRememberMe] = useState(true);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ═══ FUNNEL: ARRIVING HERE IS NOT A SIGN-UP START (cc_tasks row 284, T319) ═══
  // Until 2026-09-30 arriving at /auth?next=<public page> was recorded as signup_start.
  // Every public page carries a plain /auth?next= link, so JavaScript-running crawlers
  // were counted as sign-up starts (2026-09-29: 33 "starts", 0 people). Arrival is now
  // signup_page/view (a bot's view is dropped by the server, and the funnel does not
  // count it as started); signup_start is recorded by handleSignUp and the OAuth buttons.
  useEffect(() => {
    if (session) return;
    recordSignupPageView();
    // Once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ CAPTURE UTM PARAMS IMMEDIATELY ON MOUNT — before any auth action ═══
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const domain = params.get("domain");
    const plan = params.get("plan");
    if (ref) localStorage.setItem("mf_ref", ref);
    if (domain) localStorage.setItem("mf_domain", domain);
    if (plan) localStorage.setItem("mf_plan", plan);
    // Sign-up leaves through an email link and Google sign-in returns to a bare
    // origin, so the query string is gone by the time anyone comes back. The
    // stash getRewardTarget() already falls back to is filled here rather than
    // adding a second one beside it.
    stashNext(params.get("next") || params.get("return_to"));
    // persona is captured in the top-of-component persona const via the
    // same query-string, and persisted post-signup to profiles.first_touch_persona.
  }, []);

  // ── THE SOURCE THAT PRODUCED THE ACCOUNT ────────────────────────────────
  //
  // Arrivals are cheap; accounts are not. This is the number that decides
  // whether a directory submission was worth making, and until now nothing
  // recorded it — showwork_runs.referrer said "mentionfox.com" on every row,
  // because a run starts from our own landing page.
  //
  // first-touch.js captured the real source on the visitor's FIRST arrival at
  // any public page, which may have been days and a dozen pages ago. This
  // carries that record onto the profile.
  //
  // WRITTEN ONCE, NEVER UPDATED. A second sign-in, a password reset, a re-auth
  // after a token expiry all reach this code, and any of them would otherwise
  // rewrite the account's origin to whatever brought them back. An account's
  // first touch is a fact about how it was created and it cannot change
  // afterwards.
  //
  // THE GUARD IS IN THE DATABASE, not here. This used to read
  // profiles.first_touch_source first and skip when it was set, but the browser
  // role has no SELECT on that column, so the read always failed, came back
  // empty, and every sign-in rewrote the row. The trigger
  // profiles_first_touch_family now keeps the stored first touch once it exists,
  // and derives first_touch_family from first_touch_landing.
  //
  // It never blocks anything. A failure here costs one row of analytics; it
  // must not cost somebody their sign-in, so the whole thing is in a try and
  // nothing downstream reads its result.
  // The ?next= this signup carries, read WITHOUT consuming it (getRewardTarget
  // consumes the stash later, to route). Internal paths only, the same guard.
  function currentSignupNext(): string | null {
    const ok = (v: string | null) => {
      const s = (v || "").trim();
      if (!s.startsWith("/") || s.startsWith("//") || s.startsWith("/\\") || /^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
      return s.split("#")[0].slice(0, 300) || null;
    };
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = ok(params.get("next") || params.get("return_to"));
      if (fromUrl) return fromUrl;
    } catch { /* ignore */ }
    try {
      const at = Number(localStorage.getItem("mf_next_at") || "0");
      if (at > 0 && Date.now() - at < 30 * 60 * 1000) return ok(localStorage.getItem("mf_next"));
    } catch { /* ignore */ }
    return null;
  }

  // T344: the destination an email link (magic link, confirmation) should carry back to /auth.
  // T1105: the tail of every email link back to /auth: next= plus what the signup came for (ref, domain, plan), so it
  // survives a confirmation opened in another browser. The query string first, then this browser's store.
  function emailReturnQuery(): string {
    const n = getSignupNextForEmail();
    let q: URLSearchParams | null = null;
    try { q = new URLSearchParams(window.location.search); } catch { q = null; }
    return (n ? `&next=${encodeURIComponent(n)}` : "") + intentQuery((param, key) => {
      const fromUrl = q ? q.get(param) : null;
      if (fromUrl) return fromUrl;
      try { return localStorage.getItem(key); } catch { return null; }
    });
  }

  function getSignupNextForEmail(): string | null {
    try {
      const q = new URLSearchParams(window.location.search);
      return sanitizeNext(q.get("next") || q.get("return_to")) || peekStoredNext();
    } catch { return null; }
  }

  // The anonymous visitor id first-touch.js keeps, or null when there is none.
  function currentVisitorId(): string | null {
    try {
      const ft = (window as unknown as { mfFirstTouch?: () => Record<string, string> }).mfFirstTouch?.();
      const v = ft?.visitor_id || "";
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v : null;
    } catch { return null; }
  }

  async function recordFirstTouch(userId: string) {
    // THE PAGE THAT PRODUCED THE ACCOUNT (2026-09-25): profiles.signup_next.
    // An email signup writes it server-side in register-account. Google and
    // magic-link signups come back through here, so a NEW account (created in the
    // last 30 minutes) records it now. An older account signing in via ?next=
    // did not sign up there, so it writes nothing; the database also keeps a
    // stored value once it exists.
    try {
      const next = currentSignupNext();
      if (next) {
        const { data: sess } = await supabase.auth.getSession();
        const created = Date.parse(sess?.session?.user?.created_at || "");
        if (Number.isFinite(created) && Date.now() - created < 30 * 60 * 1000) {
          // Awaited, because the redirects below can leave the app; checked, so a
          // refused write is visible in the console rather than silent.
          const { error: nextErr } = await supabase.from("profiles").update({ signup_next: next } as never).eq("id", userId);
          if (nextErr) console.debug("[signup_next] not saved", nextErr.message);
        }
      }
    } catch { /* attribution is never worth a failed sign-in */ }
    // THE CALL TO ACTION THAT PRODUCED THE ACCOUNT (2026-09-27, cc_tasks row 31).
    // page-events.js keeps the last tracked CTA clicked on a public page (page path,
    // CTA id, time) in this browser. A NEW account (created in the last 30 minutes)
    // records it if it was clicked in the last 7 days. The family is the page's first
    // path segment; the daily roll-up re-derives it with public.page_family.
    try {
      const raw = localStorage.getItem("mf_last_cta");
      const cta = raw ? JSON.parse(raw) as { page?: string; cta?: string; at?: string } : null;
      const clicked = Date.parse(cta?.at || "");
      if (cta?.page && cta?.cta && Number.isFinite(clicked) && Date.now() - clicked < 7 * 86400_000) {
        const { data: sess } = await supabase.auth.getSession();
        const created = Date.parse(sess?.session?.user?.created_at || "");
        if (Number.isFinite(created) && Date.now() - created < 30 * 60 * 1000) {
          const page = String(cta.page).slice(0, 300);
          ignoreSupabaseError(
            supabase.from("profiles").update({
              signup_cta_family: page.split("/").filter(Boolean)[0] || null,
              signup_cta_page: page,
              signup_cta_id: String(cta.cta).slice(0, 80),
              signup_cta_at: new Date(clicked).toISOString(),
            } as never).eq("id", userId),
            "best-effort signup attribution; a lost row never blocks sign-in",
          );
        }
      }
    } catch { /* attribution is never worth a failed sign-in */ }
    try {
      const ft = (window as unknown as { mfFirstTouch?: () => Record<string, string> }).mfFirstTouch?.();
      if (!ft || !ft.source) return;
      await supabase.from("profiles").update({
        first_touch_source: ft.source || null,
        first_touch_medium: ft.medium || null,
        first_touch_campaign: ft.campaign || null,
        first_touch_referrer: ft.referrer || null,
        // Path only. first_touch_family is derived from it in the database
        // (public.page_family via trigger), so the mapping lives in one place.
        first_touch_landing: ft.landing || null,
        first_touch_at: ft.at || null,
        // The anonymous visitor id the public page views carry, so this account
        // joins to the sessions that came before it. Written once, like the rest:
        // the same trigger keeps it once a first touch is stored.
        first_touch_visitor_id: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ft.visitor_id || "")
          ? ft.visitor_id : null,
      } as never).eq("id", userId);
    } catch { /* attribution is never worth a failed sign-in */ }
  }

  // After auth: check for Twitter UTM and update profile + redirect
  async function handlePostAuth(userId: string, onboardingCompleted: boolean) {
    // T344: once per sign-in. See postAuthFor.
    if (postAuthFor.current === userId) return;
    postAuthFor.current = userId;
    // T344: where they were going, read NOW, before any await below can let the address change.
    // ?next= / ?return_to= first, else the stash written before a LinkedIn/Google round trip or
    // an email link (src/lib/post-auth-route.ts), else a fresh homepage subject.
    const rewardTarget = getRewardTarget();
    // BEFORE every redirect below, and awaited, because several of them leave
    // the app outright — a fire-and-forget write would be cancelled mid-flight
    // by the exact paths that matter most.
    await recordFirstTouch(userId);
    // Same reason: who invited this account. Server-side and once; bounded so a
    // slow answer never holds the redirect. InviteClaimer retries on next load.
    try {
      await Promise.race([claimStoredInvite(), new Promise((r) => setTimeout(r, 6000))]);
    } catch { /* attribution is never worth a failed sign-in */ }
    // Same reason, same place: a page saved on a /funding page while logged out
    // lives in localStorage until there is an account to attach it to. Several
    // redirects below are full document loads, so this has to finish first or the
    // save the visitor made is the thing that gets cancelled.
    try {
      const { data: sess } = await supabase.auth.getSession();
      await flushPendingFundingSaves(sess?.session?.access_token || null);
    } catch { /* a failed flush leaves the stash intact for next time */ }
    // T366: companies refused at the free watch limit wait in watch_pending. The plan
    // purchase adds them (trigger on the tier change); this is the fallback at sign-in.
    // Bounded like the invite claim above so it can never hold the redirect.
    try {
      await Promise.race([(supabase as any).rpc("fulfil_my_pending_watches"), new Promise((r) => setTimeout(r, 4000))]);
    } catch { /* a pending watch stays pending for the next sign-in */ }
    // REWARD FIRST: if the visitor came for a specific page (e.g. a snapshot on their subject),
    // deliver it before anything else — never gate the reward behind onboarding. Onboarding still
    // runs afterward on the next onboarding-gated navigation. Consume-and-clear so it can't misfire.
    // A check they already watched run on the front page. Attach it to the account
    // they just made and hand it back BEFORE anything is asked of them. This is the
    // whole point of the order: they came with a question, they get the answer, and
    // onboarding waits its turn.
    const runToken = getShowworkRun();
    if (runToken) {
      // WHERE THEY LAND IS DECIDED BY WHAT THEY EARNED, AND THE SERVER DECIDES
      // IT. Four goals earn four different rewards; only two of them create
      // anything in the outreach queue. Somebody who came to size up a rival or
      // to read a verdict has an empty pipeline, and dropping them in front of
      // it is how a delivered reward reads as nothing happening. The claim
      // response carries `landing`; the old hard-coded report URL is the
      // fallback, which is also the right answer for every non-outreach reward.
      let landing = `/?run=${encodeURIComponent(runToken)}&claimed=1`;
      try {
        const { data: sess } = await supabase.auth.getSession();
        const tok = sess?.session?.access_token;
        if (tok) {
          const res = await fetch(`${SUPABASE_FN_BASE}/showwork-run`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ op: "claim", run_token: runToken }),
          });
          const claimed = await res.json().catch(() => null);
          if (claimed && typeof claimed.landing === "string" && claimed.landing.startsWith("/")) {
            landing = claimed.landing;
          }
        }
      } catch { /* the run is still readable by its token — never block the login on this */ }
      // ── A WAITING INTENT OUTRANKS THE CLAIM'S OWN LANDING ────────────────
      //
      // MEASURED, and it is the exact failure this is here to stop: a visitor
      // picked a second question on the front door, was sent to sign up, and
      // came back to `/?run=…&claimed=1` — the claim branch's landing — with
      // their pick silently discarded. The claim had won because it runs first
      // and `showwork` is also stashed in localStorage by the gate, so removing
      // it from the URL would not have helped.
      //
      // The claim itself is worth keeping: it attaches the run to the new
      // account. Only the DESTINATION changes. The front door reads the stored
      // pick, runs it, shows the answer, and only then offers the dashboard.
      try {
        const pend = JSON.parse(localStorage.getItem("mf_pending_intent") || "null");
        const fresh = pend && pend.angle && Number(pend.at || 0) > 0 &&
          (Date.now() - Number(pend.at)) < 2 * 60 * 60 * 1000;
        if (fresh) landing = `/?run=${encodeURIComponent(pend.run || runToken)}&intent=1`;
      } catch { /* a malformed stash is simply no intent */ }
      try { localStorage.removeItem("mf_showwork_run"); } catch { /* ignore */ }
      try { localStorage.removeItem("mf_pending_subject"); localStorage.removeItem("mf_pending_subject_at"); } catch { /* ignore */ }
      // T531: the claimed check IS the destination. Without this the onboarding gate in App.tsx
      // sent a brand-new account from its claimed report to /onboarding 0.1 s after it opened,
      // although the claim button promises "your report opens first, before anything else is asked of you".
      if (!onboardingCompleted) markOnboardingDeferred();
      window.location.href = landing;
      return;
    }

    // ── T344: THE ROUTER ORDER. Destination first, onboarding after. ────────
    // decidePostAuthRoute (src/lib/post-auth-route.ts) is the whole rule and is unit tested
    // (scripts/post-auth-route-test.mjs). An account with onboarding unfinished that is sent to
    // its destination is marked deferred for this tab session, so the onboarding gates in
    // App.tsx, ProtectedRoute and the page-level checks let it through and AppLayout offers
    // "Finish setup" as a banner instead of pulling it into the wizard.
    // T821: "Watch these companies" on /buyers sends next=/buyers?run=<token>&watch=1; a sign-in that lost the address
    // (an email link opened elsewhere) still lands there from the browser's own copy (mf_buyers_watch, 2 hours).
    const route = decidePostAuthRoute({ next: rewardTarget || getBuyersWatch(), storedNext: null, onboardingCompleted });
    if (route.kind === "next") {
      try { localStorage.removeItem("mf_pending_subject"); localStorage.removeItem("mf_pending_subject_at"); } catch { /* ignore */ }
      if (route.deferOnboarding) markOnboardingDeferred();
      // Most destinations (/pricing, /, /company/..., the landing pages) are static documents the
      // React router has never heard of: navigate() rendered NotFound, which is how /pricing was
      // lost on 2026-09-30. Those are full page loads; only the app's own sections stay in-app.
      if (route.fullLoad) { window.location.replace(route.path); return; }
      navigate(route.path, { replace: true });
      return;
    }

    const ref = localStorage.getItem("mf_ref") || "";
    const domain = localStorage.getItem("mf_domain") || "";

    // Check if profile already has twitter onboarding set
    const { data: profile } = await supabase.from("profiles").select("onboarding_type").eq("id", userId).maybeSingle();
    const existingType = (profile as any)?.onboarding_type;

    // If Twitter UTM detected and onboarding not yet completed
    if (ref.startsWith("tw_") && existingType !== "twitter_completed") {
      // Write UTM to profile
      await supabase.from("profiles").update({
        acquisition_source: "twitter",
        twitter_referrer: ref,
        referred_domain: domain,
        onboarding_type: "twitter",
      } as any).eq("id", userId);
      // Clean up
      localStorage.removeItem("mf_ref");
      localStorage.removeItem("mf_domain");
      navigate("/onboarding-twitter", { replace: true });
      return;
    }

    // If profile was already set to twitter onboarding (e.g. by SQL) and not completed
    if (existingType === "twitter" && !onboardingCompleted) {
      navigate("/onboarding-twitter", { replace: true });
      return;
    }

    // ── WHAT THEY CAME TO BUY, BEFORE ANYTHING ELSE ────────────────────
    //
    // /pricing has linked Pro to /auth?plan=pro and Agency to
    // /auth?plan=agency all along, and this page read that param for
    // ATTRIBUTION only — first_touch_plan — then routed to onboarding or the
    // dashboard like any other signup. Somebody who clicked "Start Pro",
    // entered an email and a password and expected to pay was shown a
    // dashboard, with no mention of the thing they had just chosen.
    //
    // Ahead of the onboarding redirect on purpose: a person who came to buy
    // should reach the payment page, not a setup wizard. Onboarding is still
    // there when they come back from Stripe.
    try {
      const intent = takeCheckoutIntent();
      if (intent) {
        const { url, error: checkoutError } = await resumeCheckout(supabase, intent);
        if (url) {
          // T344: back from the payment page, setup is offered, not forced.
          if (!onboardingCompleted) markOnboardingDeferred();
          window.location.href = url;
          return;
        }
        // Never a dead click. Say what happened and carry on to the app rather
        // than stranding them on a spinner.
        toast({
          title: "We could not open the payment page",
          description: `${checkoutError || "Please try again."} Your account is set up — you can start this from Billing.`,
          variant: "destructive",
        });
      }
    } catch (e) {
      console.error("[checkout-intent] resume failed:", e);
    }

    // No destination: onboarding if it is not finished (decidePostAuthRoute's second rule).
    if (route.kind === "onboarding") {
      navigate(route.path, { replace: true });
      return;
    }

    // DEN-SWITCHER-PILL (2026-05-17) — route by active Den. Zero Dens →
    // /dashboard (pill shows "Pick your Den" CTA). One+ Dens → land on
    // the persona-appropriate surface: investors and real-estate
    // investors deep-link straight into Foxhole; everyone else gets
    // /dashboard/den (the universal Den home).
    try {
      const { data: dens } = await supabase
        .from("user_active_dens" as any)
        .select("persona_key, is_primary")
        .eq("user_id", userId);
      const rows = (dens as any) || [];
      if (rows.length === 0) {
        navigate("/dashboard", { replace: true });
        return;
      }
      const primary = rows.find((r: any) => r.is_primary) || rows[0];
      const key = primary?.persona_key;
      if (key === "investor" || key === "real_estate_investor") {
        navigate("/dashboard/foxhole", { replace: true });
      } else {
        navigate("/dashboard/my-den", { replace: true });
      }
    } catch {
      navigate("/dashboard", { replace: true });
    }
  }

  // If already logged in, redirect immediately — no login form flash
  useEffect(() => {
    if (!isReady) return;
    if (session && user) {
      (async () => {
        const result = await ensureProfile(user);
        recordOAuthSignupCompleted(user, persona);
        await handlePostAuth(user.id, result.onboardingCompleted);
      })();
    }
  }, [isReady, session, user, navigate]);

  // Listen for new sign-ins (Google OAuth callback, etc.)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (event === "SIGNED_IN" && newSession) {
          const provider = newSession.user.app_metadata?.provider;
          if (provider === "google") {
            storeLastAuthMethod(newSession.user.email || "", "google");
          } else if (provider === "linkedin_oidc") {
            storeLastAuthMethod(newSession.user.email || "", "linkedin");
          } else if (newSession.user.email) {
            storeLastAuthMethod(newSession.user.email, mode === "magic_link" ? "magic_link" : "email");
          }

          setTimeout(async () => {
            const result = await ensureProfile(newSession.user);
            recordOAuthSignupCompleted(newSession.user, persona);
            // Write referral attribution if a ?via= cookie is in localStorage.
            try {
              const { writeAttributionIfPresent } = await import("@/lib/viralAttribution");
              await writeAttributionIfPresent(newSession.user.id, "signup");
            } catch { /* swallow */ }
            await handlePostAuth(newSession.user.id, result.onboardingCompleted);
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, mode]);

  // Smart returning user detection on email blur
  const handleEmailBlur = useCallback(async () => {
    if (!email || email.length < 5) return;

    const result = magicLinkSchema.safeParse({ email });
    if (!result.success) return;

    setCheckingUser(true);
    try {
      const userCheck = await checkExistingUser(email);
      setReturningUser(userCheck);
    } catch {
      // Silently fail - non-critical feature
    } finally {
      setCheckingUser(false);
    }
  }, [email]);

  // Persona captured from /geofixer-trial's ?persona= param. Survives the
  // redirect to /auth because landing-page JS appends it to the CTA URLs.
  // Feeds into signup analytics so we can measure conversion rate per
  // persona variant. Invalid values fall back to 'solopreneur' on
  // landing; here we just pass through whatever arrived.
  const persona = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('persona');
      const VALID = ['solopreneur','saas-founder','agency-owner','pr-lead','ecommerce','enterprise'];
      return p && VALID.includes(p) ? p : 'solopreneur';
    } catch { return 'solopreneur'; }
  })();

  // Handle Google OAuth
  // T319: through the auth server, the same destination-keeping path as LinkedIn. The old
  // route (the Lovable broker at /~oauth/initiate) is a 404 on this host.
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const err = await startOAuthSignIn("google", { persona });

      if (err) {
        toast({
          title: "Google sign in failed",
          description: err,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Google sign in failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle Magic Link
  const handleMagicLink = async () => {
    setErrors({});
    const result = magicLinkSchema.safeParse({ email });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: result.data.email,
        options: {
          // T344: back through /auth so the post-auth order runs, with ?next= when there is one.
          emailRedirectTo: (() => {
            // T1105: next= and the signup's intent, on the link itself.
            const tail = emailReturnQuery();
            return `${window.location.origin}/auth${tail ? `?${tail.slice(1)}` : ""}`;
          })(),
          // Only used when this creates the account: the signup browser's
          // visitor id, for last touch (see handleSignUp).
          data: { mf_visitor_id: currentVisitorId() },
        },
      });

      if (error) throw error;

      setMagicLinkSent(true);
      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link.",
      });
    } catch (error) {
      toast({
        title: "Failed to send magic link",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Email/Password Sign In
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = signInSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: result.data.email,
        password: result.data.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setErrors({ password: "Invalid email or password" });
        } else if (error.message.includes("Email not confirmed")) {
          setErrors({ email: "This email is not confirmed yet. Open the link we sent when you signed up, or get a new one below." });
          setUnconfirmedEmail(result.data.email);
          setUnconfirmedSent(false);
        } else {
          throw error;
        }
        return;
      }

      storeLastAuthMethod(result.data.email, "email");
      // Honor "Keep me signed in" (default ON = persistent). Reuses the existing
      // persistent_login / mf_session_ephemeral mechanism — OFF clears the session
      // on tab close (useAuth beforeunload); ON persists across browser restarts
      // (the default). Mirrors the Security settings toggle so the two stay in sync.
      try {
        if (rememberMe) window.localStorage.removeItem("mf_session_ephemeral");
        else window.localStorage.setItem("mf_session_ephemeral", "true");
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { error: writeError } = await supabase.from("profiles").update({ persistent_login: rememberMe } as any).eq("id", session.user.id);
          // A failed write must not reach the success message below.
          if (writeError) throw writeError;
        }
      } catch { /* non-fatal — default-persistent still applies */ }
    } catch (error) {
      toast({
        title: "Sign in failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Email/Password Sign Up
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    let honeypot = "";
    try {
      const hp = (e.currentTarget as HTMLFormElement).elements.namedItem("website_confirm") as HTMLInputElement | null;
      honeypot = hp?.value || "";
    } catch { /* no field: empty */ }

    const result = signUpSchema.safeParse({
      email,
      password,
      fullName,
      companyName: companyName || undefined
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // The funnel's signup_start (T319): the form was submitted and passed validation.
    recordSignupStart("email");

    setLoading(true);
    try {
      // NOT supabase.auth.signUp. That is one GoTrue call which creates the
      // user and sends the confirmation in a single transaction, so a refused
      // address or a Resend outage rolls the account back and answers 500 — the
      // registration is simply lost, and it looks like nobody signed up.
      // register-account creates the account first and treats the mail as a
      // separate, non-fatal step.
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/register-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({
          // T332 honeypot: a hidden field no person sees or fills; register-account refuses a filled one.
          website_confirm: honeypot,
          email: result.data.email,
          password: result.data.password,
          full_name: result.data.fullName,
          company_name: result.data.companyName,
          // The browser that SIGNED UP, captured at the moment of signup. Last
          // touch is derived from this visitor's page views in the database
          // (trigger profiles_last_touch), because the first sign-in after an
          // email confirmation can happen in a different browser.
          mf_visitor_id: currentVisitorId(),
          // The invite this browser followed, kept on the account so a
          // confirmation opened in another browser still records the inviter.
          invite_token: readStoredInviteRef(),
          // Where the account came from, saved with the account itself
          // (profiles.signup_next and first_touch_*), so it survives a
          // confirmation opened in a different browser.
          signup_next: currentSignupNext(),
          first_touch: (() => {
            try { return (window as unknown as { mfFirstTouch?: () => Record<string, string> }).mfFirstTouch?.() || null; }
            catch { return null; }
          })(),
          redirect_to: `${window.location.origin}/auth?verified=true${emailReturnQuery()}`,
        }),
      });
      const out = await resp.json().catch(() => null);

      if (out?.code === "already_registered") {
        setErrors({ email: "An account with this email already exists. Sign in below, or use Forgot password." });
        setMode("signin");
        return;
      }
      // T573: a password the account service refuses is shown under the password field.
      if (out?.code === "weak_password") {
        setErrors({ password: out.error || "Choose a different password." });
        return;
      }
      if (!resp.ok || !out?.ok) {
        throw new Error(out?.error || "We could not create your account just now. Try again in a minute.");
      }

      // THE ACCOUNT EXISTS EITHER WAY from here down. Only the mail is in doubt.
      if (out.email_sent === false) {
        setResendEmail(result.data.email);
        setConfirmSendFailed(true);
        toast({
          title: "Account created — we couldn't send the email",
          description: "Your account is ready. Use the resend button to get your confirmation link.",
          variant: "destructive",
        });
        return;
      }

      trackEvent('signup_completed', { method: 'email', persona });

      // First-touch attribution — stamp plan + persona onto the profile row
      // so downstream conversion analytics can measure which variant converted.
      // Fire-and-forget; signup success doesn't depend on this landing.
      try {
        const plan = (new URLSearchParams(window.location.search).get("plan") || localStorage.getItem("mf_plan") || "").slice(0, 64);
        const { data: { user: newUser } } = await supabase.auth.getUser();
        if (newUser?.id) {
          const { error: writeError2 } = await supabase.from("profiles")
            .update({ first_touch_plan: plan || null, first_touch_persona: persona })
            .eq("id", newUser.id);
          // A failed write must not reach the success message below.
          if (writeError2) throw writeError2;
        }
      } catch { /* non-critical */ }
      // T534: the panel replaces the form (no toast: the panel says it, in place).
      setSignupSentTo(result.data.email);
    } catch (error) {
      toast({
        title: "Sign up failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // T534: what this sign-up is for, from the page they were sent here from (null when none).
  const destCopy = signupDestinationCopy(getSignupNextForEmail());

  // T534: send the confirmation again to the address the panel shows.
  const resendSignupConfirmation = async () => {
    if (!signupSentTo) return;
    setResending(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/register-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({ action: "resend", email: signupSentTo, redirect_to: `${window.location.origin}/auth?verified=true${emailReturnQuery()}` }),
      });
      const j = await r.json().catch(() => null);
      if (j?.email_sent) toast({ title: "Sent again", description: `Check your email at ${signupSentTo}.` });
      else toast({ title: "Still could not send", description: "Try again in a minute.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  // Show spinner while auth is resolving — prevents login form flash
  if (!isReady || (session && user)) {
    return (
      <div className="min-h-screen bg-auth-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-auth-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-auth-muted text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-auth-background flex items-center justify-center p-4 pb-56 sm:pb-48">{/* T573: room under the form so the cookie notice never covers Sign in or Create account */}
      {/* Background gradient effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-auth-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Email verified banner */}
        {window.location.search.includes("verified=true") && (
          <div className="mb-4 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-center">
            <p className="text-sm text-emerald-400 font-medium">Email verified! Sign in to continue.</p>
          </div>
        )}
        {/* T332: a LinkedIn/Google sign-in that did not finish. ?next= is kept, so trying again returns them there. */}
        {oauthNotice && (
          <div role="alert" className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-center">
            <p className="text-sm text-amber-300 font-medium">{oauthNotice}</p>
          </div>
        )}
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <a href="https://mentionfox.com">
              <img src={mentionfoxLogo} alt="MentionFox" className="h-10 w-auto" />
            </a>
          </div>
          <p className="text-auth-muted">
            {mode === "signup"
              ? (destCopy?.line || "Create your account to start monitoring")
              : "Welcome back! Sign in to continue"}
          </p>
        </div>

        {/* Main card */}
        <div className="relative">
          {/* Animated gradient border */}
          <div className="absolute -inset-[1px] bg-gradient-to-r from-auth-accent via-purple-500 to-auth-accent rounded-xl opacity-20 blur-sm animate-pulse" />

          <div className="relative bg-auth-card rounded-xl border border-auth-border p-8">
            {/* Returning user message */}
            {returningUser?.exists && returningUser.lastAuthMethod && (
              <div className="mb-6 p-3 rounded-lg bg-auth-accent/10 border border-auth-accent/20 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-auth-accent flex-shrink-0" />
                <p className="text-sm text-auth-foreground">
                  Welcome back! You usually sign in with{" "}
                  <span className="font-semibold text-auth-accent">
                    {getAuthMethodLabel(returningUser.lastAuthMethod)}
                  </span>
                </p>
              </div>
            )}

            {confirmSendFailed ? (
              /* THE ACCOUNT EXISTS. Only the mail did not go out, and saying so
                 plainly is the whole point — the old path lost the registration
                 and showed a generic failure. */
              <div className="text-center space-y-4 py-4">
                <h2 className="text-xl font-semibold text-auth-foreground">Your account is ready</h2>
                <p className="text-auth-muted text-sm">
                  We could not send the confirmation email to <strong>{resendEmail}</strong> just now.
                  Nothing is lost — your account exists. Send it again to finish.
                </p>
                <Button
                  className="w-full"
                  disabled={resending}
                  onClick={async () => {
                    setResending(true);
                    try {
                      const r = await fetch(`${SUPABASE_URL}/functions/v1/register-account`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}` },
                        body: JSON.stringify({ action: "resend", email: resendEmail, redirect_to: `${window.location.origin}/auth?verified=true${emailReturnQuery()}` }),
                      });
                      const j = await r.json().catch(() => null);
                      if (j?.email_sent) {
                        setConfirmSendFailed(false);
                        setMagicLinkSent(true);
                        toast({ title: "Sent", description: "Check your email for the confirmation link." });
                      } else {
                        toast({ title: "Still could not send", description: "Try again in a minute, or sign in once it arrives.", variant: "destructive" });
                      }
                    } finally {
                      setResending(false);
                    }
                  }}
                >
                  {resending ? "Sending…" : "Resend confirmation email"}
                </Button>
                <Button
                  variant="ghost"
                  className="text-auth-muted hover:text-auth-foreground"
                  onClick={() => { setConfirmSendFailed(false); setMode("signin"); }}
                >
                  Back to sign in
                </Button>
              </div>
            ) : signupSentTo ? (
              /* T534: the account is made; the only thing left is the email. Said in place, not in a corner. */
              <div className="text-center py-8" data-foxchat-id="auth-signup-check-email">
                <div className="w-16 h-16 rounded-full bg-auth-success/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-auth-success" />
                </div>
                <h2 className="text-xl font-semibold text-auth-foreground mb-2" title="Your account is made. Open the email to finish.">
                  Check your email at <span className="break-all">{signupSentTo}</span>.
                </h2>
                <p className="text-auth-muted mb-6">
                  {destCopy ? `The link brings you straight back to ${destCopy.what}.` : "The link signs you in."}
                </p>
                <Button
                  className="w-full mb-3"
                  disabled={resending}
                  onClick={() => void resendSignupConfirmation()}
                  title={`Sends the confirmation email to ${signupSentTo} again`}
                >
                  {resending ? "Sending…" : "Resend confirmation email"}
                </Button>
                <button
                  type="button"
                  className="text-auth-accent hover:underline text-sm"
                  onClick={() => setSignupSentTo(null)}
                  title="Back to the form with the address you typed, so you can correct it"
                >
                  Wrong address? Change it
                </button>
              </div>
            ) : magicLinkSent ? (
              // Magic link sent confirmation
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-auth-success/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-auth-success" />
                </div>
                <h2 className="text-xl font-semibold text-auth-foreground mb-2">
                  Check your email
                </h2>
                <p className="text-auth-muted mb-6">
                  We've sent a magic link to <span className="text-auth-foreground">{email}</span>
                </p>
                <Button
                  variant="ghost"
                  className="text-auth-muted hover:text-auth-foreground"
                  onClick={() => setMagicLinkSent(false)}
                >
                  Use a different email
                </Button>
              </div>
            ) : (
              <>
                {/* Google OAuth - shown only while the auth server has Google on (T319); first of Google, LinkedIn, email, with a tooltip (T345) */}
                {googleOn && (
                <Button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  title={GOOGLE_TOOLTIP}
                  aria-label="Continue with Google"
                  className={`w-full h-12 mb-4 bg-white hover:bg-gray-50 text-gray-900 font-medium transition-all duration-200 ${
                    returningUser?.lastAuthMethod === "google"
                      ? "ring-2 ring-auth-accent ring-offset-2 ring-offset-auth-card"
                      : ""
                  }`}
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <GoogleIcon />
                      <span className="ml-2">Continue with Google</span>
                    </>
                  )}
                </Button>
                )}

                {/* LinkedIn sign-in (cc_tasks row 260). Same return path as Google:
                    ?next=, the reward stash and a checkout intent all survive the trip. */}
                <LinkedInSignInButton
                  persona={persona}
                  className={`w-full h-12 mb-4 bg-white hover:bg-gray-50 text-gray-900 font-medium transition-all duration-200 ${
                    returningUser?.lastAuthMethod === "linkedin"
                      ? "ring-2 ring-auth-accent ring-offset-2 ring-offset-auth-card"
                      : ""
                  }`}
                />

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-auth-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-auth-card px-2 text-auth-muted">or continue with email</span>
                  </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={mode === "signup" ? handleSignUp : handleSignIn}>
                  {/* T332 honeypot: off-screen, not focusable, hidden from screen readers. */}
                  {mode === "signup" && (
                    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                      <label htmlFor="website_confirm">Leave this empty</label>
                      <input id="website_confirm" name="website_confirm" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
                    </div>
                  )}
                  <div className="space-y-4">
                    {/* Full Name - Sign Up only */}
                    {mode === "signup" && (
                      <div className="space-y-2">
                        <Label htmlFor="fullName" className="text-auth-foreground">
                          Full Name
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-auth-muted" />
                          <Input
                            id="fullName"
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="pl-10 h-11 bg-auth-background border-auth-border text-auth-foreground placeholder:text-auth-muted focus:border-auth-border-focus focus:ring-auth-accent/20 transition-all duration-200"
                            placeholder="John Doe"
                          />
                        </div>
                        {errors.fullName && (
                          <p className="text-sm text-auth-error">{errors.fullName}</p>
                        )}
                      </div>
                    )}

                    {/* Company Name - Sign Up only */}
                    {mode === "signup" && (
                      <div className="space-y-2">
                        <Label htmlFor="companyName" className="text-auth-foreground">
                          Company Name <span className="text-auth-muted">(optional)</span>
                        </Label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-auth-muted" />
                          <Input
                            id="companyName"
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            className="pl-10 h-11 bg-auth-background border-auth-border text-auth-foreground placeholder:text-auth-muted focus:border-auth-border-focus focus:ring-auth-accent/20 transition-all duration-200"
                            placeholder="Acme Inc."
                          />
                        </div>
                      </div>
                    )}

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-auth-foreground">
                        Email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-auth-muted" />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onBlur={handleEmailBlur}
                          className={`pl-10 h-11 bg-auth-background border-auth-border text-auth-foreground placeholder:text-auth-muted focus:border-auth-border-focus focus:ring-auth-accent/20 transition-all duration-200 ${
                            errors.email ? "border-auth-error" : ""
                          }`}
                          placeholder="you@company.com"
                        />
                        {checkingUser && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-auth-muted animate-spin" />
                        )}
                      </div>
                      {errors.email && (
                        <p className="text-sm text-auth-error">{errors.email}</p>
                      )}
                      {mode === "signin" && unconfirmedEmail && (
                        unconfirmedSent ? (
                          <p className="text-sm text-auth-muted">Sent. Check {unconfirmedEmail} (and the spam folder), open the link, and you are signed in.</p>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={resending}
                            title={`Sends the confirmation link to ${unconfirmedEmail} again`}
                            onClick={async () => {
                              setResending(true);
                              try {
                                const r = await fetch(`${SUPABASE_URL}/functions/v1/register-account`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}` },
                                  // Same return as the sign-up's own link: back through /auth, with the page they came from (?next=).
                                  body: JSON.stringify({ action: "resend", email: unconfirmedEmail, redirect_to: `${window.location.origin}/auth?verified=true${emailReturnQuery()}` }),
                                });
                                const j = await r.json().catch(() => null);
                                if (j?.email_sent) { setUnconfirmedSent(true); setErrors({}); }
                                else toast({ title: "Could not send just now", description: "Try again in a minute.", variant: "destructive" });
                              } finally {
                                setResending(false);
                              }
                            }}
                          >
                            {resending ? "Sending…" : "Send the confirmation link again"}
                          </Button>
                        )
                      )}
                    </div>

                    {/* Password */}
                    {mode !== "magic_link" && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-auth-foreground">
                            Password
                          </Label>
                          {mode === "signin" && (
                            <Link
                              to={(() => { const n = getSignupNextForEmail(); return `/forgot-password${n ? `?next=${encodeURIComponent(n)}` : ""}`; })()}
                              title="Emails you a link to set a new password"
                              className="text-xs text-auth-accent hover:underline"
                            >
                              Forgot password?
                            </Link>
                          )}
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-auth-muted" />
                          <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`pl-10 h-11 bg-auth-background border-auth-border text-auth-foreground placeholder:text-auth-muted focus:border-auth-border-focus focus:ring-auth-accent/20 transition-all duration-200 ${
                              errors.password ? "border-auth-error" : ""
                            }`}
                            placeholder={mode === "signup" ? "Min. 8 characters" : "Enter password"}
                          />
                        </div>
                        {errors.password && (
                          <p className="text-sm text-auth-error">{errors.password}</p>
                        )}
                        {mode === "signup" && (
                          <p className="text-xs text-auth-muted">
                            At least 8 characters. Any letters, numbers or symbols.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Remember Me - Sign In only */}
                    {mode === "signin" && (
                      <div className="flex items-center gap-2">
                        <input
                          id="rememberMe"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 rounded border-auth-border text-auth-accent focus:ring-auth-accent/20"
                        />
                        <label htmlFor="rememberMe" className="text-sm text-auth-muted cursor-pointer select-none">
                          Keep me signed in
                        </label>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      title={mode === "signup" ? "Create your account with this email and password" : "Sign in with your email and password"}
                      className="w-full h-11 bg-auth-accent hover:bg-auth-accent-hover text-white font-medium transition-all duration-200"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          {mode === "signup" ? "Create Account" : "Sign In"}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>

                {/* Magic Link Option */}
                {mode === "signin" && (
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleMagicLink}
                      disabled={loading || !email}
                      title="Email me a one-time sign-in link instead of using a password"
                      className={`w-full h-10 text-auth-muted hover:text-auth-foreground hover:bg-auth-card-hover transition-all duration-200 ${
                        returningUser?.lastAuthMethod === "magic_link"
                          ? "ring-1 ring-auth-accent/50"
                          : ""
                      }`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Magic Link
                    </Button>
                  </div>
                )}

                {/* Toggle Mode */}
                <div className="mt-6 text-center">
                  <p className="text-auth-muted text-sm">
                    {mode === "signup" ? (
                      <>
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("signin")}
                          className="text-auth-accent hover:underline font-medium"
                          title="Switch to signing in with an account you already have"
                        >
                          Sign in
                        </button>
                      </>
                    ) : (
                      <>
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("signup")}
                          className="text-auth-accent hover:underline font-medium"
                          title="Switch to creating a new free account"
                        >
                          Create one
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-auth-muted text-xs mt-8">
          By continuing, you agree to our{" "}
          <a href="/legal" className="text-auth-accent hover:underline">Terms of Service</a>
          {" "}and{" "}
          <a href="/privacy" className="text-auth-accent hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
